import type { TypedDocumentNode } from '@apollo/client';
import {
  initialPagedGridInput,
  PAGED_LIST_FETCH_POLICY,
  type ServerEnhancedGridState,
  usePreloadedServerGridQuery,
} from 'src/modules/grids';
import { preloadRouteQuery } from 'src/routing/routePreload';
import type { AppRouterContext } from 'src/routing/routerAccess';
import type { PricingSheetsPageQuery, PricingSheetsPageQueryVariables } from 'types/graphql';

/** Where the list persists its column state; its tab loader seeds the first page from it. v2: server-paged. */
export const PRICING_SHEETS_GRID_STORAGE_KEY = 'pricing-sheets_v2';

export const PRICING_SHEETS_PAGE_QUERY: TypedDocumentNode<PricingSheetsPageQuery, PricingSheetsPageQueryVariables> =
  gql`
    query PricingSheetsPageQuery($input: StandardPageInput) {
      pricingSheetPage(input: $input) {
        totalCount
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            id
            draft
            enabled
            name
            customerGroups {
              id
              name
            }
            individualCustomers {
              id
              name
            }
            startDate
            expiryDate
            pricingFormula
          }
        }
      }
    }
  `;

/** The pricing-sheet list, one server page at a time. */
export function usePricingSheetsPage(gridState: ServerEnhancedGridState) {
  return usePreloadedServerGridQuery({
    queryKey: 'pricingSheetPage',
    query: PRICING_SHEETS_PAGE_QUERY,
    gridState,
    label: 'pricing sheets',
  });
}

/** The list's first page as the grid will ask for it; the Business Setup tab loader preloads it. */
export function preloadPricingSheetsGrid(context: AppRouterContext) {
  return preloadRouteQuery(
    context,
    PRICING_SHEETS_PAGE_QUERY,
    { input: initialPagedGridInput(PRICING_SHEETS_GRID_STORAGE_KEY) },
    { fetchPolicy: PAGED_LIST_FETCH_POLICY }
  );
}
